/**
 * Utilities for managing assistants in Supabase
 * @module assistantUtils
 */

import { supabase } from "@/lib/supabase";
import { fileUtils } from "@/lib/utils/fileUtils.js";

const TABLE_NAME = "assistants";
export const AssistantSharing = {
  PRIVATE: "private",
  PUBLIC: "public",
  WORKSPACE: "workspace",
};
export const DefaultFileMap = {
  general: [],
  retrieval: [],
  code_interpreter: [],
};
export const DefaultToolResources = {
  file_search: { vector_store_ids: [] },
  code_interpreter: { file_ids: [] },
};
const BATCH_SIZE = 50;
const DELAY_MS = 1000;
const REQUIRED_FIELDS = [
  "id",
  "user_id",
  "assistant_id",
  "workspace_id",
  "assistant_title",
  "assistant_description",
  "assistant_model",
  "created_at",
  "updated_at",
  "workspace_ids",
];

// ----------------------------------------------
// UTILITY FUNCTIONS
// ----------------------------------------------
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const isValidFileId = (fileId) => {
  if (!fileId || typeof fileId !== "string") return false;
  return fileId.startsWith("file-");
};

const parseDate = (timestamp) => {
  if (!timestamp) return new Date().toISOString();
  if (typeof timestamp === "number") {
    return new Date(timestamp * 1000).toISOString();
  }
  try {
    return new Date(timestamp).toISOString();
  } catch (e) {
    return new Date().toISOString();
  }
};
// ----------------------------------------------
// FORMAT ASSISTANT
// ----------------------------------------------
const isUUID = (str) => {
  const uuidPattern =
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidPattern.test(str);
};

const castToUUID = (value) => {
  if (!value) return null;
  if (isUUID(value)) return value;
  return null;
};

// Extract tool names from tools array
const extractToolNames = (tools = []) => {
  if (!Array.isArray(tools)) return [];

  return tools
    .map((tool) => {
      if (tool.type === "function") {
        return tool.function?.name;
      }
      return tool.type;
    })
    .filter(Boolean);
};

// ----------------------------------------------
// CLEANUP FUNCTIONS
// ----------------------------------------------
async function cleanupDeletedAssistants(existingIds, openaiIds) {
  const assistantsToDelete = Array.from(existingIds).filter(
    (id) => !openaiIds.has(id)
  );
  let deletedCount = 0;

  if (assistantsToDelete.length === 0) return 0;

  // Update workspaces in batches
  const { data: affectedWorkspaces, error: workspaceQueryError } =
    await supabase
      .from("workspaces")
      .select("id, assistant_id")
      .in("assistant_id", assistantsToDelete);

  if (workspaceQueryError) {
    console.error("Error querying affected workspaces:", workspaceQueryError);
    return 0;
  }

  const WORKSPACE_BATCH_SIZE = 10;
  for (let i = 0; i < affectedWorkspaces.length; i += WORKSPACE_BATCH_SIZE) {
    const batch = affectedWorkspaces.slice(i, i + WORKSPACE_BATCH_SIZE);
    const workspaceIds = batch.map((w) => w.id);

    const { error: workspaceUpdateError } = await supabase
      .from("workspaces")
      .update({ assistant_id: null })
      .in("id", workspaceIds);

    if (workspaceUpdateError) {
      console.error("Error updating workspace batch:", workspaceUpdateError);
      continue;
    }
    await delay(DELAY_MS);
  }

  // Delete assistants in batches
  const CLEANUP_BATCH_SIZE = 5;
  for (let i = 0; i < assistantsToDelete.length; i += CLEANUP_BATCH_SIZE) {
    const batch = assistantsToDelete.slice(i, i + CLEANUP_BATCH_SIZE);

    const { error: deleteError } = await supabase
      .from(TABLE_NAME)
      .delete()
      .in("id", batch);

    if (deleteError) {
      console.error("Error deleting assistants:", deleteError);
    } else {
      deletedCount += batch.length;
    }

    await delay(DELAY_MS);
  }

  return deletedCount;
}

export function formatOpenAIAssistant(assistant, userId, workspaceId) {
  if (!assistant || typeof assistant !== "object") {
    throw new Error("Invalid assistant object");
  }

  const {
    id,
    name,
    description,
    model,
    instructions,
    tools = [],
    file_ids = [],
    metadata = {},
    avatar_url,
    response_format,
    top_p,
    temperature,
  } = assistant;

  // Extract tool names from tools array
  const toolNames = extractToolNames(tools);

  // Initialize workspace_ids array
  let workspace_ids = [];

  // Check if workspace_ids is in metadata
  if (metadata && Array.isArray(metadata.workspace_ids)) {
    workspace_ids = [...metadata.workspace_ids];
  }

  // Add current workspace_id to workspace_ids if provided and not already included
  if (workspaceId && !workspace_ids.includes(workspaceId)) {
    workspace_ids.push(workspaceId);
  }

  // Validate and clean metadata
  const validatedMetadata = {
    ...metadata,
    user_id: String(userId), // Ensure user_id is a string
    workspace_id: workspaceId,
    created_at: metadata?.created_at || new Date().toISOString(),
    updated_at: metadata?.updated_at || new Date().toISOString(),
    // JSON stringify workspace_ids for metadata to avoid type error
    workspace_ids: JSON.stringify(workspace_ids),
  };

  return {
    ...(!id?.startsWith("asst_") ? { id } : {}),
    assistant_id: null,
    workspace_id: workspaceId,
    workspace_ids: workspace_ids,
    name: name || "Untitled Assistant",
    description: description || null,
    model: model || "gpt-4.1-mini",
    instructions: instructions || null,
    tools: tools, // Store the full tools array
    tool_names: toolNames,
    avatar_url: avatar_url || null,
    metadata: validatedMetadata,
    is_active: true,
    response_format: response_format?.type || null,
    top_p: typeof top_p === "number" ? top_p : null,
    temperature: typeof temperature === "number" ? temperature : null,
    thread_ids: [],
    usage_count: 0,
    image_path: "",
    sharing: AssistantSharing.PRIVATE,
    image_url: "",
    user_id: String(userId), // Ensure user_id is a string
    created_at: parseDate(metadata?.created_at),
    updated_at: parseDate(metadata?.updated_at),
  };
}

// ----------------------------------------------
// BATCH PROCESSING
// ----------------------------------------------
async function processBatch(assistants, userId, workspaceId) {
  const results = [];
  const errors = [];

  for (const assistant of assistants) {
    try {
      if (!assistant?.id?.startsWith("asst_")) {
        console.warn("Skipping invalid assistant:", assistant);
        continue;
      }

      // Format assistant for upsert with proper UUID casting
      const formattedAssistant = {
        user_id: userId,
        workspace_id: workspaceId,
        name: assistant.name || "Untitled Assistant",
        description: assistant.description || null,
        model: assistant.model || "gpt-4.1-mini",
        instructions: assistant.instructions || null,
        tools: assistant.tools || [], // Store the full tools array
        is_active: true,
        metadata: assistant.metadata || {},
        response_format: assistant.response_format?.type || null,
        top_p: typeof assistant.top_p === "number" ? assistant.top_p : null,
        temperature:
          typeof assistant.temperature === "number"
            ? assistant.temperature
            : null,
        sharing: "private",
        updated_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
        thread_ids: [],
        usage_count: 0,
        image_path: "",
        image_url: "",
      };

      // Insert new record
      const { error: insertError } = await supabase
        .from("assistants")
        .upsert(formattedAssistant, {
          onConflict: "id",
          ignoreDuplicates: false,
        });

      if (insertError) throw insertError;
      results.push(formattedAssistant);
    } catch (error) {
      console.error("Error processing assistant:", error);
      errors.push({
        assistant_id: assistant.id,
        error: error.message,
      });
    }
  }

  return { results, errors };
}

async function syncAssistant(assistant, userId, workspaceId) {
  try {
    // Format the assistant data according to the schema
    const assistantData = {
      user_id: userId,
      workspace_id: workspaceId,
      name: assistant.name,
      description: assistant.description || "",
      model: assistant.model,
      instructions: assistant.instructions || "",
      tools: assistant.tools || [], // Store the full tools array
      metadata: assistant.metadata || {},
      is_active: true,
      response_format: assistant.response_format?.type || null,
      top_p: assistant.top_p || null,
      temperature: assistant.temperature || null,
      sharing: "private",
    };

    // Use upsert with the correct configuration
    const { data, error } = await supabase
      .from("assistants")
      .insert([assistantData])
      .select();

    if (error) {
      throw error;
    }

    return data;
  } catch (error) {
    console.error("Error syncing assistant:", error);
    throw error;
  }
}

// ----------------------------------------------
// MAIN SYNC FUNCTION
// ----------------------------------------------
export const formatAndSyncAssistants = async ({
  openaiAssistants,
  supabaseAssistants,
  userId,
  workspaceId,
}) => {
  const results = [];

  for (const assistant of openaiAssistants) {
    try {
      const result = await syncAssistant(assistant, userId, workspaceId);
      results.push(result);
    } catch (error) {
      console.error(`Failed to sync assistant ${assistant.id}:`, error);
      results.push({
        assistantId: assistant.id,
        error: error.message,
      });
    }
  }
  return results;
};

/**
 * Create a new assistant record
 * @param {Object} assistantData Assistant data to create
 * @returns {Promise<Object>} Created assistant record
 */
export const createAssistant = async (assistantData) => {
  try {
    // Get personal workspace ID if no workspace_id provided
    let workspaceId = assistantData.workspace_id;
    if (!workspaceId) {
      // Import authStore to get personal workspace ID
      const { useAuthStore } = await import("@/store/authStore");
      const authStore = useAuthStore.getState();
      workspaceId = authStore.getPersonalWorkspaceId();

      if (!workspaceId) {
        throw new Error("No workspace available for assistant creation");
      }
    }

    // Ensure workspace_ids is an array
    const workspace_ids = Array.isArray(assistantData.workspace_ids)
      ? assistantData.workspace_ids
      : [];

    // Add the workspace ID to workspace_ids if not already included
    if (!workspace_ids.includes(workspaceId)) {
      workspace_ids.push(workspaceId);
    }

    // Extract tool names if tools exist
    const toolNames = assistantData.tools
      ? extractToolNames(assistantData.tools)
      : [];

    // Create a metadata object with stringified workspace_ids
    const metadata = {
      ...(assistantData.metadata || {}),
      created_at: new Date().toISOString(),
      // JSON stringify workspace_ids for metadata
      workspace_ids: JSON.stringify(workspace_ids),
      // Ensure user_id is a string
      user_id: String(assistantData.user_id || ""),
    };

    const formattedAssistant = {
      id: fileUtils.generateUUID(),
      assistant_id: null,
      workspace_id: workspaceId, // Use the determined workspace ID
      workspace_ids: workspace_ids,
      user_id: String(assistantData.user_id || ""), // Ensure user_id is a string
      name: assistantData.name,
      description: assistantData.description,
      model: assistantData.model,
      instructions: assistantData.instructions,
      tools: assistantData.tools || [], // Store the full tools array
      tool_names: toolNames, // Add tool_names array
      file_ids: assistantData.file_ids || [],
      avatar_url: assistantData.avatar_url,
      metadata: metadata,
      is_active: assistantData.is_active ?? true,
      response_format: assistantData.response_format,
      top_p: assistantData.top_p,
      temperature: assistantData.temperature,
      thread_ids: assistantData.thread_ids || [],
      usage_count: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from(TABLE_NAME)
      .insert(formattedAssistant)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error creating assistant:", error);
    throw error;
  }
};

/**
 * Get an assistant record
 * @param {string} assistantId - Assistant ID
 * @returns {Promise<Object>} Assistant record
 */
export const getAssistant = async (assistantId) => {
  try {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .select("*")
      .eq("id", assistantId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error retrieving assistant:", error);
    throw error;
  }
};

/**
 * Update an assistant record
 * @param {string} assistantId - Assistant ID
 * @param {Object} updates - Updates to apply
 * @returns {Promise<Object>} Updated assistant record
 */
export const updateAssistant = async (assistantId, updates) => {
  try {
    // Extract tool names if tools exist in updates
    const toolNames = updates.tools ? extractToolNames(updates.tools) : null;

    const updateData = {
      ...updates,
      updated_at: new Date().toISOString(),
      metadata: {
        ...updates.metadata,
        updated_at: new Date().toISOString(),
      },
    };

    // Only add tool_names if tools were updated
    if (toolNames !== null) {
      updateData.tool_names = toolNames;
    }

    // Store the full tools array if provided
    if (updates.tools) {
      updateData.tools = updates.tools;
    }

    // Ensure workspace_ids is always an array
    if (updates.workspace_ids && !Array.isArray(updates.workspace_ids)) {
      if (typeof updates.workspace_ids === "string") {
        try {
          updateData.workspace_ids = JSON.parse(updates.workspace_ids);
        } catch {
          updateData.workspace_ids = [];
        }
      } else {
        updateData.workspace_ids = [];
      }
    }

    const { data, error } = await supabase
      .from(TABLE_NAME)
      .update(updateData)
      .eq("id", assistantId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error updating assistant:", error);
    throw error;
  }
};

/**
 * List assistants
 * @param {Object} options - List options
 * @returns {Promise<Array>} Array of assistant records
 */
export const listAssistants = async (options = {}) => {
  try {
    const { userId, workspaceId, isActive } = options;

    // Start with base query
    let query = supabase.from(TABLE_NAME).select("*");

    // Add filters
    if (userId) {
      query = query.eq("user_id", userId);
    }

    // if (workspaceId) {
    //   // Check if we're filtering by primary workspace_id or by workspace_ids array
    //   if (options.useWorkspaceIdsArray) {
    //     // Filter where workspace_ids array contains the workspaceId
    //     query = query.contains("workspace_ids", [workspaceId]);
    //   } else {
    //     // Filter by primary workspace_id field
    //     query = query.eq("workspace_id", workspaceId);
    //   }
    // }

    if (isActive !== undefined) {
      query = query.eq("is_active", isActive);
    }

    // Add ordering - use created_at by default
    query = query.order("created_at", { ascending: false });

    // Execute query
    const { data, error } = await query;

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error listing assistants:", error);
    throw error;
  }
};

// Batch update assistants
export const batchUpdateAssistants = async (updates) => {
  const { data, error } = await supabase
    .from(TABLE_NAME)
    .upsert(
      Object.entries(updates).map(([id, update]) => ({
        assistant_id: id,
        ...update,
        updated_at: new Date().toISOString(),
      }))
    )
    .select();

  if (error) throw error;
  return data;
};

// Add an assistant to the database
export const addAssistant = async (assistantData) => {
  try {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .insert({
        ...assistantData,
        created_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error adding assistant:", error);
    throw error;
  }
};

// Add multiple assistants to the database
export const addAssistants = async (assistants) => {
  try {
    // Format assistants with proper types
    const formattedAssistants = assistants.map((assistant) => ({
      ...assistant,
      // Primary key as UUID
      id: assistant.id || fileUtils.generateUUID(),
      // Basic fields
      assistant_id: null,
      workspace_id: assistant.workspace_id,
      user_id: assistant.user_id,
      name: assistant.name,
      description: assistant.description,
      model: assistant.model,
      instructions: assistant.instructions,
      tools: assistant.tools || [], // Store the full tools array
      // Optional fields with defaults
      is_active: assistant.is_active ?? true,
      response_format: assistant.response_format || null,
      top_p: assistant.top_p || 1,
      temperature: assistant.temperature || 1,
      // Metadata and timestamps
      metadata: {
        ...assistant.metadata,
        synced_at: new Date().toISOString(),
      },
      created_at: assistant.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }));

    // Perform upsert with proper conflict handling
    const { data, error } = await supabase
      .from("assistants")
      .upsert(formattedAssistants, {
        onConflict: "id",
        returning: true,
        ignoreDuplicates: false,
        // Update these fields when there's a conflict
        update: {
          name: "EXCLUDED.name",
          description: "EXCLUDED.description",
          model: "EXCLUDED.model",
          instructions: "EXCLUDED.instructions",
          tools: "EXCLUDED.tools", // Include tools in updates
          metadata: "EXCLUDED.metadata",
          updated_at: "EXCLUDED.updated_at",
        },
      });

    if (error) {
      console.error("Supabase upsert error:", error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error("Error adding assistants:", error);
    throw error;
  }
};

/**
 * Delete an assistant record
 * @param {string} assistantId - Assistant ID
 * @returns {Promise<boolean>} True if deletion was successful
 */
export const deleteAssistant = async (assistantId) => {
  try {
    const { error } = await supabase
      .from(TABLE_NAME)
      .delete()
      .eq("id", assistantId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error("Error deleting assistant:", error);
    throw error;
  }
};

/**
 * Update assistant tools
 * @param {string} assistantId - Assistant ID
 * @param {Array} tools - Tools to update
 * @returns {Promise<Object>} Updated assistant record
 */
export const updateAssistantTools = async (assistantId, tools) => {
  try {
    if (!assistantId) {
      throw new Error("Assistant ID is required");
    }

    // Extract tool names from tools array
    const toolNames = extractToolNames(tools);

    const updateData = {
      tools: tools, // Store the full tools array
      tool_names: toolNames, // Add tool_names array
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from("assistants")
      .update(updateData)
      .eq("id", assistantId)
      .select()
      .single();

    if (error) {
      console.error("Supabase update error:", error);
      throw error;
    }

    return { assistant: data, error: null };
  } catch (error) {
    console.error("Error updating assistant tools:", error);
    return { assistant: null, error };
  }
};

/**
 * Increment assistant usage count
 * @param {string} assistantId - Assistant ID
 * @returns {Promise<Object>} Updated assistant record
 */
export const incrementUsageCount = async (assistantId) => {
  try {
    const { data, error } = await supabase.rpc("increment_assistant_usage", {
      assistant_id: assistantId,
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error incrementing usage count:", error);
    throw error;
  }
};

/**
 * Update assistant thread IDs
 * @param {string} assistantId - Assistant ID
 * @param {Array} threadIds - Thread IDs to update
 * @returns {Promise<Object>} Updated assistant record
 */
export const updateThreadIds = async (assistantId, threadIds) => {
  try {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .update({
        thread_ids: threadIds,
        updated_at: new Date().toISOString(),
      })
      .eq("id", assistantId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error updating thread IDs:", error);
    throw error;
  }
};

// Update assistants
export const updateAssistants = async (assistantsData) => {
  try {
    // Keep tools arrays in all assistants data
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .upsert(assistantsData)
      .select();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error updating assistants:", error);
    throw error;
  }
};

/**
 * Add a workspace ID to an assistant's workspace_ids array
 * @param {string} assistantId - Assistant ID
 * @param {string} workspaceId - Workspace ID to add
 * @returns {Promise<Object>} Updated assistant record
 */
export const addWorkspaceToAssistant = async (assistantId, workspaceId) => {
  try {
    // Ensure IDs are strings
    const assistantIdStr = String(assistantId);
    const workspaceIdStr = String(workspaceId);

    // First get the current assistant to check existing workspace_ids
    const { data: assistant, error: getError } = await supabase
      .from(TABLE_NAME)
      .select("workspace_ids, metadata")
      .eq("id", assistantIdStr)
      .single();

    if (getError) throw getError;

    // Initialize workspace_ids array or use existing one
    let workspace_ids = Array.isArray(assistant.workspace_ids)
      ? [...assistant.workspace_ids]
      : [];

    // Add workspace ID if not already included
    if (!workspace_ids.includes(workspaceIdStr)) {
      workspace_ids.push(workspaceIdStr);
    }

    // Ensure metadata is an object
    const metadata =
      typeof assistant.metadata === "object" && assistant.metadata !== null
        ? { ...assistant.metadata }
        : {};

    // Ensure user_id is a string if it exists
    if (metadata.user_id) {
      metadata.user_id = String(metadata.user_id);
    }

    // Update the assistant
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .update({
        workspace_ids,
        updated_at: new Date().toISOString(),
        metadata: {
          ...metadata,
          // JSON stringify the workspace_ids for metadata to avoid type error
          workspace_ids: JSON.stringify(workspace_ids),
          updated_at: new Date().toISOString(),
        },
      })
      .eq("id", assistantIdStr)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error adding workspace to assistant:", error);
    throw error;
  }
};

/**
 * Remove a workspace ID from an assistant's workspace_ids array
 * @param {string} assistantId - Assistant ID
 * @param {string} workspaceId - Workspace ID to remove
 * @returns {Promise<Object>} Updated assistant record
 */
export const removeWorkspaceFromAssistant = async (
  assistantId,
  workspaceId
) => {
  try {
    // Ensure IDs are strings
    const assistantIdStr = String(assistantId);
    const workspaceIdStr = String(workspaceId);

    // First get the current assistant to check existing workspace_ids
    const { data: assistant, error: getError } = await supabase
      .from(TABLE_NAME)
      .select("workspace_ids, metadata")
      .eq("id", assistantIdStr)
      .single();

    if (getError) throw getError;

    // Initialize workspace_ids array or use existing one
    let workspace_ids = Array.isArray(assistant.workspace_ids)
      ? [...assistant.workspace_ids]
      : [];

    // Remove workspace ID
    workspace_ids = workspace_ids.filter((id) => String(id) !== workspaceIdStr);

    // Ensure metadata is an object
    const metadata =
      typeof assistant.metadata === "object" && assistant.metadata !== null
        ? { ...assistant.metadata }
        : {};

    // Ensure user_id is a string if it exists
    if (metadata.user_id) {
      metadata.user_id = String(metadata.user_id);
    }

    // Update the assistant
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .update({
        workspace_ids,
        updated_at: new Date().toISOString(),
        metadata: {
          ...metadata,
          // JSON stringify the workspace_ids for metadata to avoid type error
          workspace_ids: JSON.stringify(workspace_ids),
          updated_at: new Date().toISOString(),
        },
      })
      .eq("id", assistantIdStr)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error removing workspace from assistant:", error);
    throw error;
  }
};

/**
 * List assistants by workspace ID
 * @param {string} workspaceId - Workspace ID
 * @returns {Promise<Array>} Array of assistant records
 */
export const listAssistantsByWorkspace = async (workspaceId) => {
  try {
    // Query assistants where workspace_ids array contains the workspaceId
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .select("*")
      .contains("workspace_ids", [workspaceId])
      .order("created_at", { ascending: false });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error listing assistants by workspace:", error);
    throw error;
  }
};

// At the bottom of the file, export the AssistantUtils object
export const SupabaseAssistantUtils = {
  createAssistant,
  getAssistant,
  updateAssistant,
  listAssistants,
  batchUpdateAssistants,
  addAssistant,
  addAssistants,
  deleteAssistant,
  updateAssistantTools,
  incrementUsageCount,
  updateThreadIds,
  updateAssistants,
  formatOpenAIAssistant,
  formatAndSyncAssistants,
  addWorkspaceToAssistant,
  removeWorkspaceFromAssistant,
  listAssistantsByWorkspace,
};

export default SupabaseAssistantUtils;
